/**
 * Document-level learning token pointer interaction — ports extension
 * `features/learning/learning.js` gesture (`.unisubs-token` + practice track gate).
 */
import type { Settings } from '@core/settings/schema.ts';
import { joinSegmentRange } from './segmentSubtitleText.ts';
import { isPracticeLearningSlot } from './learningHoverPause.ts';
import type { SubtitleSlot } from '../../state/subtitleStore.ts';

export type LearningTokenInteractionDeps = {
  getSettings: () => Pick<Settings, 'learningMode' | 'dictationSubtitle'>;
  onSelect: (state: {
    selectedText: string;
    slot: SubtitleSlot;
    clientX: number;
    clientY: number;
  }) => void;
  getDocument?: () => Document;
};

type SegmentRef = {
  el: HTMLElement;
  text: string;
  isWordLike?: boolean;
  index: number;
};

function collectCueSegments(cueRoot: Element): SegmentRef[] {
  return Array.from(cueRoot.querySelectorAll('[data-token-index]')).map((node) => {
    const el = node as HTMLElement;
    return {
      el,
      text: el.dataset.plain ?? el.textContent ?? '',
      isWordLike: el.classList.contains('unisubs-token'),
      index: Number.parseInt(el.dataset.tokenIndex || '0', 10),
    };
  });
}

function highlightTokenRange(segments: SegmentRef[], from: number, to: number): void {
  const start = Math.min(from, to);
  const end = Math.max(from, to);
  segments.forEach((seg) => {
    if (!seg.isWordLike) return;
    seg.el.classList.toggle('is-selected', seg.index >= start && seg.index <= end);
  });
}

function clearTokenSelection(root: Element): void {
  root.querySelectorAll('.unisubs-token.is-selected').forEach((el) => {
    el.classList.remove('is-selected');
  });
}

/** Prefer elementFromPoint; fall back to geometry when over gaps / PE-none layers. */
function tokenAtPoint(
  cueRoot: Element,
  segments: SegmentRef[],
  clientX: number,
  clientY: number,
  doc: Document,
): HTMLElement | null {
  const hit = doc.elementFromPoint(clientX, clientY);
  const fromPoint = hit?.closest?.('.unisubs-token') as HTMLElement | null;
  if (fromPoint && cueRoot.contains(fromPoint)) return fromPoint;

  let best: HTMLElement | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const seg of segments) {
    if (!seg.isWordLike) continue;
    const rect = seg.el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    const inside =
      clientX >= rect.left
      && clientX <= rect.right
      && clientY >= rect.top
      && clientY <= rect.bottom;
    if (inside) return seg.el;
    // Allow a small vertical band so finger drift between lines still tracks.
    const cx = Math.min(Math.max(clientX, rect.left), rect.right);
    const cy = Math.min(Math.max(clientY, rect.top), rect.bottom);
    const dist = (clientX - cx) ** 2 + (clientY - cy) ** 2;
    if (dist < bestDist && dist <= 24 * 24) {
      bestDist = dist;
      best = seg.el;
    }
  }
  return best;
}

export function installLearningTokenInteraction(
  deps: LearningTokenInteractionDeps,
): () => void {
  const doc = () => deps.getDocument?.() ?? document;
  let active: {
    pointerId: number;
    captureEl: HTMLElement;
    subContainer: HTMLElement;
    cueRoot: Element;
    segments: SegmentRef[];
    startIndex: number;
    endIndex: number;
    dragged: boolean;
  } | null = null;

  const endGesture = (ev: PointerEvent) => {
    if (!active || ev.pointerId !== active.pointerId) return;
    window.removeEventListener('pointermove', onMove, true);
    window.removeEventListener('pointerup', endGesture, true);
    window.removeEventListener('pointercancel', endGesture, true);
    try {
      active.captureEl.releasePointerCapture?.(ev.pointerId);
    } catch {
      // ignore
    }

    const { segments, startIndex, endIndex, dragged, subContainer, cueRoot } = active;
    const selectedText = joinSegmentRange(
      segments.map((s) => ({ text: s.text, isWordLike: s.isWordLike })),
      startIndex,
      endIndex,
    ).trim();
    clearTokenSelection(cueRoot);
    active = null;

    if (selectedText && (dragged || startIndex === endIndex)) {
      const slot: SubtitleSlot = subContainer.id === 'secondSub' ? 'second' : 'main';
      deps.onSelect({
        selectedText,
        slot,
        clientX: ev.clientX,
        clientY: ev.clientY,
      });
    }
  };

  const onMove = (ev: PointerEvent) => {
    if (!active || ev.pointerId !== active.pointerId) return;
    // Keep the browser from scrolling / cancelling the multi-token drag.
    if (ev.cancelable) ev.preventDefault();
    const token = tokenAtPoint(
      active.cueRoot,
      active.segments,
      ev.clientX,
      ev.clientY,
      doc(),
    );
    if (!token) return;
    const idx = Number.parseInt(token.dataset.tokenIndex || '0', 10);
    if (Number.isNaN(idx)) return;
    if (idx !== active.endIndex) {
      active.dragged = active.dragged || idx !== active.startIndex;
      active.endIndex = idx;
      highlightTokenRange(active.segments, active.startIndex, active.endIndex);
    }
  };

  const onPointerDown = (ev: PointerEvent) => {
    const settings = deps.getSettings();
    if (!settings.learningMode) return;
    if (ev.button != null && ev.button !== 0) return;
    if ((ev.target as Element | null)?.closest?.('.unisubs-sub-drag-handle')) return;

    const token = (ev.target as Element | null)?.closest?.('.unisubs-token') as HTMLElement | null;
    if (!token) return;

    const subContainer = token.closest('#mainSub,#secondSub') as HTMLElement | null;
    if (!subContainer) return;
    const slot: SubtitleSlot = subContainer.id === 'secondSub' ? 'second' : 'main';
    if (!isPracticeLearningSlot(settings, slot)) return;

    const cueRoot = token.closest('.textBasedSub') || subContainer;
    const segments = collectCueSegments(cueRoot);
    const startIndex = Number.parseInt(token.dataset.tokenIndex || '0', 10);
    if (Number.isNaN(startIndex)) return;

    ev.preventDefault();
    clearTokenSelection(subContainer);

    active = {
      pointerId: ev.pointerId,
      captureEl: token,
      subContainer,
      cueRoot,
      segments,
      startIndex,
      endIndex: startIndex,
      dragged: false,
    };

    highlightTokenRange(segments, startIndex, startIndex);
    try {
      token.setPointerCapture?.(ev.pointerId);
    } catch {
      // ignore
    }
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', endGesture, true);
    window.addEventListener('pointercancel', endGesture, true);
  };

  const root = doc();
  root.addEventListener('pointerdown', onPointerDown, true);
  return () => {
    root.removeEventListener('pointerdown', onPointerDown, true);
    window.removeEventListener('pointermove', onMove, true);
    window.removeEventListener('pointerup', endGesture, true);
    window.removeEventListener('pointercancel', endGesture, true);
  };
}

/** Test helper: resolve which token a drag point would hit. */
export const __testing = { tokenAtPoint, collectCueSegments, highlightTokenRange };
